import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { Field } from 'formik';
import _ from 'lodash';
import { DateModel } from '@tradingblock/types';
import {
  BaseFieldProps,
  FloatingLabelWrapper,
  FloatingLabelState,
  SelectDropdownField,
  Text,
  isFieldValueFilled,
} from '@tradingblock/components';
import dayjs from 'dayjs';

interface DateFieldProps extends BaseFieldProps {
  showErrorMessage?: boolean;
  futureOnly?: boolean;
}

export const isDateFilled = (value: DateModel | null | undefined) => {
  return (
    !!value && (isFieldValueFilled(value.month) || isFieldValueFilled(value.day) || isFieldValueFilled(value.year))
  );
};

export const getDatePartOptions = (
  part: keyof DateModel,
  forPastDates?: boolean,
  futureOnly?: boolean,
  selectedDate?: DateModel
) => {
  const today = dayjs();
  const min = part === 'year' ? (forPastDates ? 1900 : futureOnly ? today.year() : dayjs().year()) : 1;
  const max = part === 'year' ? (forPastDates ? today.year() : 2030) : part === 'day' ? 31 : 12;

  let options = _.map(_.range(min, max + 1), v => v);

  if (part === 'year' && forPastDates) {
    return _.reverse(options);
  }

  if (futureOnly) {
    if (part === 'year') {
      options = options.filter(year => {
        // If a month is selected, check if that month/year combination is in the future
        if (selectedDate && selectedDate.month) {
          const date = dayjs()
            .year(year)
            .month(selectedDate.month - 1);
          return date.isAfter(today) || date.isSame(today, 'month');
        }
        // If no month is selected, just filter by year
        return year >= today.year();
      });
    } else if (part === 'month') {
      // Only filter months if a year has been selected
      if (selectedDate && selectedDate.year) {
        const selectedYear = selectedDate.year;

        // Filter months to prevent past dates for the selected year
        options = options.filter(month => {
          const date = dayjs()
            .year(selectedYear)
            .month(month - 1);
          return date.isAfter(today) || date.isSame(today, 'month');
        });
      }
      // If no year is selected yet, show all months (no filtering needed)
    } else if (part === 'day') {
      // Only filter days if both year and month are selected
      if (selectedDate && selectedDate.year && selectedDate.month) {
        const selectedYear = selectedDate.year;
        const selectedMonth = selectedDate.month;

        options = options.filter(day => {
          const date = dayjs()
            .year(selectedYear)
            .month(selectedMonth - 1) // dayjs months are 0-based
            .date(day);
          return date.isAfter(today);
        });
      }
      // If no year or month is selected yet, show all days (no filtering needed)
    }
  }

  return options;
};

export const DateField: React.FC<DateFieldProps> = ({
  form,
  id,
  disabled,
  defaultLabelId,
  controlledLabel,
  onLabelStateChange,
  errorAfterSubmit,
  showErrorMessage,
  futureOnly,
}) => {
  const { values, touched, errors, submitCount, setFieldValue } = form;
  const value = useMemo(() => _.get(values, id), [values, id]);
  const error = _.get(touched, id) ? _.pickBy(errors, (err, key) => err && _.startsWith(key, id)) : undefined;
  const forPastDates =
    useMemo(() => _.endsWith(id, 'dateOfBirth') || _.endsWith(id, 'CreatedDate'), [id]) ||
    _.endsWith(id, 'dateOfDeath');
  const [floatingLabelState, setFloatingLabelState] = useState<FloatingLabelState>({ isFilled: isDateFilled(value) });
  const [hasUserInteracted, setHasUserInteracted] = useState(false);
  const updateFloatingLabelState = useCallback(
    (state: FloatingLabelState) => {
      setFloatingLabelState(state);
      if (onLabelStateChange) {
        onLabelStateChange(state);
      }
    },
    [onLabelStateChange]
  );

  // showError will either error after submit or default to true
  // if show error message is false, never show the error
  const showError = errorAfterSubmit ? submitCount > 0 : showErrorMessage;

  useEffect(() => {
    if (!controlledLabel) {
      updateFloatingLabelState({ isFilled: isDateFilled(value), hasFocus: floatingLabelState.hasFocus });
    }
  }, [value, controlledLabel, floatingLabelState.hasFocus, updateFloatingLabelState]);

  // Clear invalid selections when year or month changes (only after user interaction)
  useEffect(() => {
    if (futureOnly && value && (value.month || value.year)) {
      const today = dayjs();

      // Clear month if month/year combination is in the past
      if (value.month && value.year) {
        const selectedDate = dayjs()
          .year(value.year)
          .month(value.month - 1);

        if (selectedDate.isBefore(today, 'month')) {
          setFieldValue(`${id}.month`, '');
        }
      }

      // Clear year if month/year combination is in the past
      if (value.month && value.year) {
        const selectedDate = dayjs()
          .year(value.year)
          .month(value.month - 1);

        if (selectedDate.isBefore(today, 'month')) {
          setFieldValue(`${id}.year`, '');
        }
      }
    }
  }, [value && value.year, value && value.month, futureOnly, id, setFieldValue]);
  const handleLabelStateChange = (state: FloatingLabelState) => {
    updateFloatingLabelState({
      isFilled: isDateFilled(value),
      hasFocus: !_.isUndefined(state.hasFocus) ? state.hasFocus : floatingLabelState.hasFocus,
    });
  };
  const getFieldProps = (key: keyof DateModel) => {
    return {
      component: SelectDropdownField,
      id: `${id}.${key}`,
      options: getDatePartOptions(key, forPastDates, futureOnly, value),
      disabled,
      defaultLabelId: defaultLabelId && `${defaultLabelId}.${key}`,
      optionsDefaultLabelId: key,
      useRawOptions: key === 'day' || key === 'year',
      controlledLabel: true,
      onLabelStateChange: handleLabelStateChange,
      errorAfterSubmit: errorAfterSubmit,
    };
  };

  return (
    <>
      <FloatingLabelWrapper
        className={`flabel-multi ${!_.isEmpty(error) && showError ? 'invalid' : ''}`}
        state={floatingLabelState}
        disabled={disabled}
      >
        <div className="date-field-parts" role="group">
          <div className="date-field-parts__segment date-field-parts__segment--month">
            <Field {...getFieldProps('month')} />
          </div>
          <span className="date-field-parts__sep" aria-hidden="true">
            /
          </span>
          <div className="date-field-parts__segment date-field-parts__segment--day">
            <Field {...getFieldProps('day')} />
          </div>
          <span className="date-field-parts__sep" aria-hidden="true">
            /
          </span>
          <div className="date-field-parts__segment date-field-parts__segment--year">
            <Field {...getFieldProps('year')} />
          </div>
        </div>
      </FloatingLabelWrapper>
      {!_.isEmpty(error) && showError && (
        <span className="error">
          <Text id={id} textKey={'error'} type={'error'} />
        </span>
      )}
    </>
  );
};
